import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = [
  { key: "whatsapp", label: "واتساب", icon: MessageCircle, color: "bg-emerald-500", ring: "ring-emerald-200" },
  { key: "facebook", label: "فيسبوك", icon: Facebook, color: "bg-blue-600", ring: "ring-blue-200" },
  { key: "instagram", label: "إنستغرام", icon: Instagram, color: "bg-pink-500", ring: "ring-pink-200" },
];

export default function Social() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [links, setLinks] = useState(null);
  const [form, setForm] = useState({ whatsapp: "", facebook: "", instagram: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/social").then((r) => { setLinks(r.data); setForm(r.data); }).catch(() => setLinks({}));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/social", form); toast.success("تم حفظ الروابط"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (links === null) return <PageLoader />;
  const active = CHANNELS.filter((c) => links[c.key]);

  return (
    <>
      <PageHeader title="مواقع التواصل" subtitle="تابعنا على منصات التواصل الاجتماعي" />

      {active.length === 0 && !isAdmin ? (
        <p className="rounded-2xl border border-slate-100 bg-card p-8 text-center text-sm text-slate-400 soft-shadow">لم تتم إضافة روابط تواصل بعد.</p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-3">
          {CHANNELS.map((c) => links[c.key] ? (
            <a key={c.key} href={links[c.key]} target="_blank" rel="noreferrer" data-testid={`social-${c.key}`}
              className={`card-hover flex flex-col items-center gap-3 rounded-2xl border border-slate-100 bg-card p-8 text-center soft-shadow ring-1 ${c.ring}`}>
              <div className={`flex h-16 w-16 items-center justify-center rounded-2xl text-white ${c.color}`}><c.icon className="h-8 w-8" /></div>
              <span className="font-heading text-lg font-bold text-navy">{c.label}</span>
              <span className="truncate text-xs text-slate-400">{links[c.key]}</span>
            </a>
          ) : null)}
        </div>
      )}

      {isAdmin && (
        <div className="mt-8 rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
          <h3 className="mb-4 font-heading text-lg font-bold text-navy">تعديل الروابط</h3>
          <div className="grid gap-4">
            {CHANNELS.map((c) => (
              <div key={c.key}>
                <Label className="flex items-center gap-2"><c.icon className="h-4 w-4" /> رابط {c.label}</Label>
                <Input data-testid={`social-input-${c.key}`} value={form[c.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [c.key]: e.target.value }))} placeholder="https://..." className="mt-1.5 rounded-lg" dir="ltr" />
              </div>
            ))}
            <Button data-testid="social-save" onClick={save} disabled={saving} className="rounded-xl bg-navy hover:bg-navy-light">حفظ الروابط</Button>
          </div>
        </div>
      )}
    </>
  );
}
